import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar.js";
import {
  allUsersApi,
  bypassSingleUserApi,
  deleteEachUserApi,
  updateSignleUsersStatusApi,
  UpdateAdminPermissionsApi,
  registerSubAdminApi
} from "../../../Api/Service";
import { updateSignleUsersApi } from "../../../Api/Service";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthUser } from "react-auth-kit";
import {
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  FormControlLabel
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  ManageAccounts as ManageIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Business as CrmIcon
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';

const AdminManagementCRM = () => {
  const [users, setUsers] = useState([]);
  const [unVerified, setUnVerified] = useState([]);
  const [open, setOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [isDisable, setIsDisable] = useState(false);
  const [isUsers, setIsUsers] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const authUser = useAuthUser();
  const navigate = useNavigate();

  // CRM shell layout state (match other CRM pages)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenu, setIsMobileMenu] = useState(false);
  // Add Admin dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: `Admin@${Math.random().toString(36).slice(2,10)}`,
    phone: "",
    note: "",
    address: "",
    city: "",
    country: "",
    postalCode: "",
    currency: "USD",
    AiTradingPercentage: 1.25
  });
  // Manage Admin dialog state
  const [manageOpen, setManageOpen] = useState(false);
  const [managing, setManaging] = useState(false);
  const [editAdmin, setEditAdmin] = useState({
    _id: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    note: "",
    address: "",
    city: "",
    country: "",
    postalCode: "",
    currency: "USD",
    AiTradingPercentage: 1.25
  });

  const getAllUsers = async () => {
    try {
      // Fetch admins only with role filter
      const params = { role: 'admin', limit: 1000 };
      const resp = await allUsersApi(params);

      if (resp.success) {
        let filtered;
        let unverified;
        if (authUser().user.role === "superadmin") {
          filtered = resp.allUsers.filter((user) => {
            return user.role === "admin" && user.verified === true;
          });
          unverified = resp.allUsers.filter((user) => {
            return user.role === "admin" && user.verified === false;
          });
        } else {
          navigate('/admin/dashboard');
          return;
        }
        setUsers(filtered.reverse());
        setUnVerified(unverified.reverse());
      } else {
        toast.dismiss();
        toast.error(resp.msg || 'Failed to fetch admins');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.message || 'Error loading admins');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteEachUser = async (user) => {
    try {
      setIsDisable(true);
      const res = await deleteEachUserApi(user._id);
      if (res.success) {
        toast.dismiss();
        toast.success(res.msg || 'Admin deleted');
        setOpen(false);
        getAllUsers();
      } else {
        toast.dismiss();
        toast.error(res.msg || 'Failed to delete admin');
        setOpen(false);
        getAllUsers();
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.message || 'Error deleting admin');
    } finally {
      setIsDisable(false);
    }
  };

  const bypassSingleUser = async (user) => {
    try {
      setIsUsers(true);
      const res = await bypassSingleUserApi(user._id);
      if (res.success) {
        toast.dismiss();
        getAllUsers();
        toast.success(res.msg || 'Admin verified');
      } else {
        toast.dismiss();
        toast.error(res.msg || 'Failed to verify admin');
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.message || 'Error verifying admin');
    } finally {
      setIsUsers(false);
    }
  };

  const onOpenModal = (user) => {
    setOpen(true);
    setModalData(user);
  };
  const onCloseModal = () => setOpen(false);

  useEffect(() => {
    // Gate: Only superadmin may access CRM Admin Management
    const u = authUser()?.user;
    if (!u) {
      navigate('/auth/login/crm');
      return;
    }
    if (u.role !== 'superadmin') {
      navigate('/admin/dashboard');
      return;
    }
    getAllUsers();
  }, []);

  // CRM-only permission categories
  const [permLoad, setPermLoad] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState({}); // Track expanded state per user+category
  
  const handlePermissionChange = async (userId, key, value) => {
    setPermLoad({ userId, key });
    try {
      const body = { [key]: value };
      const res = await UpdateAdminPermissionsApi(userId, body);
      if (res.success) {
        toast.success("Permission updated");
        // ✅ Update local state instead of refetching to keep accordion open
        setUsers(prevUsers => prevUsers.map(user => {
          if (user._id === userId) {
            return {
              ...user,
              adminPermissions: {
                ...user.adminPermissions,
                [key]: value
              }
            };
          }
          return user;
        }));
      } else {
        toast.error(res.msg || "Failed to update permission");
      }
    } catch (error) {
      toast.error(error?.msg || "Failed to update permission");
    } finally {
      setPermLoad(null);
    }
  };
  
  const handleAccordionChange = (userId, categoryIndex) => (event, isExpanded) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [`${userId}-${categoryIndex}`]: isExpanded
    }));
  };

  const permissionCategories = [
    {
      name: 'CRM Access',
      icon: <CrmIcon />,
      color: '#10b981',
      permissions: [
        { key: 'accessCrm', label: 'CRM Access', desc: "Allow access to CRM dashboard and features." },
        { key: 'canManageCrmLeads', label: 'Manage Leads', desc: "Upload leads CSV and assign leads (admins only)." },
        { key: 'canUploadLeads', label: 'Upload Leads CSV', desc: "Allow uploading leads via CSV in CRM." },
        { key: 'canMakeCalls', label: 'Make Calls', desc: "Allow initiating calls from CRM." },
        { key: 'canAccessCallDashboard', label: 'Call Dashboard', desc: "Allow access to Call Dashboard." },
        { key: 'canAccessAiInstructions', label: 'AI Instructions', desc: "Allow access to AI Instructions editor." }
      ]
    }
  ];

  const darkCardStyles = {
    background: 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)',
    border: '1px solid #333',
    color: '#ffffff',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
      transform: 'translateY(-2px)'
    }
  };

  const AdminCard = ({ user, isUnverified = false }) => (
    <Card sx={{ ...darkCardStyles, borderRadius: 3, overflow: 'visible', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <Chip
          icon={isUnverified ? <WarningIcon /> : <VerifiedIcon />}
          label={isUnverified ? "Unverified" : "Verified"}
          color={isUnverified ? "warning" : "success"}
          size="small"
          variant="filled"
        />
      </Box>

      <CardHeader
        avatar={
          <Avatar
            sx={{
              width: 60,
              height: 60,
              border: '3px solid',
              borderColor: isUnverified ? 'warning.main' : 'primary.main',
              bgcolor: isUnverified ? 'warning.dark' : 'primary.dark'
            }}
          >
            <AdminIcon />
          </Avatar>
        }
        title={
          <Typography variant="h6" fontWeight={600} sx={{ color: 'grey.100' }} noWrap>
            {user.firstName} {user.lastName}
          </Typography>
        }
        subheader={
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'grey.400' }} />
              <Typography variant="body2" sx={{ color: 'grey.400' }} noWrap>
                {user.email}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ fontSize: 14, mr: 1, color: 'grey.500' }} />
              <Typography variant="caption" sx={{ color: 'grey.500' }}>
                Registered: {new Date(user.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        }
        sx={{ pb: 1 }}
      />

      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ManageIcon />}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                mb: 1,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  borderColor: 'primary.light'
                }
              }}
              onClick={() => {
                setEditAdmin({
                  _id: user._id,
                  firstName: user.firstName || "",
                  lastName: user.lastName || "",
                  email: user.email || "",
                  password: user.password || "",
                  phone: user.phone || "",
                  note: user.note || "",
                  address: user.address || "",
                  city: user.city || "",
                  country: user.country || "",
                  postalCode: user.postalCode || "",
                  currency: user.currency || "USD",
                  AiTradingPercentage: typeof user.AiTradingPercentage === 'number' ? user.AiTradingPercentage : 1.25
                });
                setManageOpen(true);
              }}
            >
              Manage Admin
            </Button>

            {isUnverified && (
              <Button
                disabled={isUsers}
                onClick={() => bypassSingleUser(user)}
                variant="contained"
                fullWidth
                startIcon={<CheckIcon />}
                sx={{
                  backgroundColor: 'warning.dark',
                  mb: 1,
                  '&:hover': {
                    backgroundColor: 'warning.main'
                  }
                }}
              >
                {isUsers ? 'Verifying...' : 'Verify Email'}
              </Button>
            )}

            {/* Delete Button for Superadmin */}
            {(authUser().user.role === "superadmin") && (
              <Button
                onClick={() => onOpenModal(user)}
                variant="outlined"
                fullWidth
                startIcon={<DeleteIcon />}
                sx={{
                  borderColor: 'error.dark',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                    borderColor: 'error.light'
                  }
                }}
              >
                Delete Admin
              </Button>
            )}
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />

          {/* CRM Permissions */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'grey.100' }}>
                CRM Permissions
              </Typography>
            </Box>

            {permissionCategories.map((category, index) => (
              <Accordion
                key={index}
                expanded={expandedAccordions[`${user._id}-${index}`] || false}
                onChange={handleAccordionChange(user._id, index)}
                sx={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '8px !important',
                  mb: 1,
                  '&:before': { display: 'none' },
                  boxShadow: 'none'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'grey.400' }} />}
                  sx={{
                    minHeight: '48px',
                    '& .MuiAccordionSummary-content': {
                      my: 1,
                      alignItems: 'center'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: `${category.color}20`,
                      color: category.color,
                      mr: 1.5
                    }}>
                      {category.icon}
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.200' }}>
                      {category.name}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={1.5}>
                    {category.permissions.map((permission) => (
                      <Box
                        key={permission.key}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 255, 255, 0.05)'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={500} sx={{ color: 'grey.100' }}>
                            {permission.label}
                          </Typography>
                          {permLoad?.userId === user._id && permLoad?.key === permission.key ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Switch
                              size="small"
                              checked={user.adminPermissions?.[permission.key] || false}
                              onChange={(e) => handlePermissionChange(user._id, permission.key, e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: category.color
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: category.color
                                }
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>
                          {permission.desc}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: "block", height: "100vh", bgcolor: "grey.50", position: "relative" }}>
      {/* Sidebar */}
      <Box>
        <Sidebar
          setisMobileMenu={setIsMobileMenu}
          isMobileMenu={isMobileMenu}
          isCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
        />
      </Box>

      {/* Overlay for mobile - closes sidebar when clicked */}
      {isMobileMenu && (
        <Box
          onClick={() => setIsMobileMenu(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1199,
            display: { xs: 'block', md: 'none' },
            cursor: 'pointer'
          }}
        />
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          ml: {
            xs: 0,
            md: isSidebarCollapsed ? "80px" : "280px",
          },
          transition: "margin-left 0.3s ease",
        }}
      >
        {/* Content */}
        {isLoading ? (
          <Box sx={{ width: '100%', p: 4, textAlign: 'center' }}>
            <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="h6" sx={{ mt: 2, color: 'grey.700' }}>
              Loading Admins...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="700">
                  Admin Management (CRM)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Superadmin can add/remove admins and manage CRM permissions
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AdminIcon />}
                onClick={() => setAddOpen(true)}
              >
                Add Admin
              </Button>
            </Box>

            {/* Verified Admins */}
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <VerifiedIcon sx={{ mr: 2, fontSize: 32, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="700" gutterBottom>
                    Verified Admins
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {users.length} admins with verified email addresses
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {users.map((user, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <AdminCard user={user} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Unverified Admins */}
            {unVerified.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <WarningIcon sx={{ mr: 2, fontSize: 32, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="h4" fontWeight="700" gutterBottom>
                      Unverified Admins
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {unVerified.length} admins pending email verification
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {unVerified.map((user, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <AdminCard user={user} isUnverified />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={open}
              onClose={() => !isDisable && onCloseModal()}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>Delete Admin</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  Are you sure you want to delete <strong>{modalData?.firstName} {modalData?.lastName}</strong>? This action cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => onCloseModal()} disabled={isDisable}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => deleteEachUser(modalData)}
                  disabled={isDisable}
                  startIcon={isDisable ? <CircularProgress size={18} /> : <DeleteIcon />}
                >
                  {isDisable ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Add Admin Dialog */}
            <Dialog
              open={addOpen}
              onClose={() => !adding && setAddOpen(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Add New Admin</DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="First Name"
                      value={newAdmin.firstName}
                      onChange={(e) => setNewAdmin({ ...newAdmin, firstName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Last Name"
                      value={newAdmin.lastName}
                      onChange={(e) => setNewAdmin({ ...newAdmin, lastName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Email"
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Password"
                      type="text"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Phone"
                      value={newAdmin.phone}
                      onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Address"
                      value={newAdmin.address}
                      onChange={(e) => setNewAdmin({ ...newAdmin, address: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="City"
                      value={newAdmin.city}
                      onChange={(e) => setNewAdmin({ ...newAdmin, city: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Country"
                      value={newAdmin.country}
                      onChange={(e) => setNewAdmin({ ...newAdmin, country: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Postal Code"
                      value={newAdmin.postalCode}
                      onChange={(e) => setNewAdmin({ ...newAdmin, postalCode: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      label="Note (optional)"
                      value={newAdmin.note}
                      onChange={(e) => setNewAdmin({ ...newAdmin, note: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAddOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      // Validate required fields similar to AddAdmin.js
                      const required = ['firstName','lastName','email','password','phone','address','city','country','postalCode'];
                      const missing = required.filter(k => !String(newAdmin[k] || '').trim());
                      if (missing.length) {
                        toast.error('Please fill all required fields');
                        return;
                      }
                      setAdding(true);
                      const body = { ...newAdmin, role: 'admin' };
                      const res = await registerSubAdminApi(body);
                      if (res.success) {
                        toast.info(res.msg || 'Admin created');
                        setAddOpen(false);
                        setNewAdmin({
                          firstName: "", lastName: "", email: "", password: "",
                          phone: "", note: "", address: "", city: "", country: "", postalCode: ""
                        });
                        getAllUsers();
                      } else {
                        toast.error(res.msg || 'Failed to create admin');
                      }
                    } catch (err) {
                      toast.error(err?.message || 'Error creating admin');
                    } finally {
                      setAdding(false);
                    }
                  }}
                  disabled={adding}
                  startIcon={adding ? <CircularProgress size={18} /> : <CheckIcon />}
                >
                  {adding ? 'Creating...' : 'Create Admin'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Manage Admin Dialog */}
            <Dialog
              open={manageOpen}
              onClose={() => !managing && setManageOpen(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Edit Admin</DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="First Name"
                      value={editAdmin.firstName}
                      onChange={(e) => setEditAdmin({ ...editAdmin, firstName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Last Name"
                      value={editAdmin.lastName}
                      onChange={(e) => setEditAdmin({ ...editAdmin, lastName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Email"
                      type="email"
                      value={editAdmin.email}
                      onChange={(e) => setEditAdmin({ ...editAdmin, email: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Password (optional)"
                      type="text"
                      value={editAdmin.password}
                      onChange={(e) => setEditAdmin({ ...editAdmin, password: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Phone"
                      value={editAdmin.phone}
                      onChange={(e) => setEditAdmin({ ...editAdmin, phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Address"
                      value={editAdmin.address}
                      onChange={(e) => setEditAdmin({ ...editAdmin, address: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="City"
                      value={editAdmin.city}
                      onChange={(e) => setEditAdmin({ ...editAdmin, city: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Country"
                      value={editAdmin.country}
                      onChange={(e) => setEditAdmin({ ...editAdmin, country: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Postal Code"
                      value={editAdmin.postalCode}
                      onChange={(e) => setEditAdmin({ ...editAdmin, postalCode: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      label="Note"
                      value={editAdmin.note}
                      onChange={(e) => setEditAdmin({ ...editAdmin, note: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setManageOpen(false)} disabled={managing}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      const required = ['firstName','lastName','email'];
                      const missing = required.filter(k => !String(editAdmin[k] || '').trim());
                      if (missing.length) {
                        toast.error('Please fill required fields (first, last, email)');
                        return;
                      }
                      setManaging(true);
                      const body = {
                        firstName: editAdmin.firstName,
                        lastName: editAdmin.lastName,
                        email: editAdmin.email,
                        phone: editAdmin.phone,
                        note: editAdmin.note,
                        address: editAdmin.address,
                        city: editAdmin.city,
                        country: editAdmin.country,
                        postalCode: editAdmin.postalCode,
                        currency: editAdmin.currency,
                        AiTradingPercentage: editAdmin.AiTradingPercentage
                      };
                      if (String(editAdmin.password || '').trim()) {
                        body.password = editAdmin.password;
                      }
                      const res = await updateSignleUsersApi(editAdmin._id, body);
                      if (res.success) {
                        toast.success(res.msg || 'Admin updated');
                        setManageOpen(false);
                        getAllUsers();
                      } else {
                        toast.error(res.msg || 'Failed to update admin');
                      }
                    } catch (err) {
                      toast.error(err?.message || 'Error updating admin');
                    } finally {
                      setManaging(false);
                    }
                  }}
                  disabled={managing}
                  startIcon={managing ? <CircularProgress size={18} /> : <CheckIcon />}
                >
                  {managing ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogActions>
            </Dialog>

          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminManagementCRM;

// Add Admin Dialog State and Handlers (placed after export to keep component clean)
// Using module-level vars would be bad; integrate into component instead:

